// Generated by GitHub Copilot
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { State, useComponentState } from '#core/state/State.ts'
import TeamsApi from '#core/api/operations/TeamsApi.ts'
import TeamCreate from '#core/entities/operations/teams/TeamCreate.ts'
import type UserCompany from '#core/entities/auth/users/UserCompany.ts'
import UserBasic from '#core/entities/auth/users/UserBasic.ts'
import type TeamMember from '#core/entities/operations/teams/TeamMember.ts'
import { showToast } from '#core/notifications/toast.ts'
import { CardModal } from '#components/primitives/components/card-modal.tsx'
import { FormField } from '#components/primitives/forms/form-field.tsx'
import { FormFooterActions } from '#components/primitives/components/form-footer-actions.tsx'
import { UserSearchSelect } from '#components/domain/auth/user/shared/user-search-select.tsx'

const teamsApi = new TeamsApi()

interface TeamEditModalProps {
  isOpen: boolean
  teamId: string | null
  users: UserCompany[]
  onClose: () => void
  onSaved: () => void
}

/** Inline state for the team create/edit modal. */
class TeamEditState extends State<TeamEditState> {
  name = ''
  description = ''
  leadId = ''
  memberIds: string[] = []
  saving = false

  /** Existing members snapshot (for diffing on save). */
  _originalMembers: TeamMember[] = []

  reset(): void {
    this.reactTo.name = ''
    this.reactTo.description = ''
    this.reactTo.leadId = ''
    this.reactTo.memberIds = []
    this.reactTo.saving = false
    this._originalMembers = []
  }

  async loadTeam(id: string): Promise<void> {
    const team = await teamsApi.getTeam(id)
    if (!team) return
    this.reactTo.name = team.name
    this.reactTo.description = team.description ?? ''
    this._originalMembers = team.members

    const lead = team.members.find(m => m.role === 'lead')
    this.reactTo.leadId = lead?.user_id ?? ''
    this.reactTo.memberIds = team.members.map(m => m.user_id)
  }

  async save(teamId: string | null, onSaved: () => void, onClose: () => void): Promise<void> {
    this.reactTo.saving = true
    try {
      let targetId = teamId

      if (targetId) {
        await teamsApi.updateTeam(targetId, { name: this.name, description: this.description || null })
      } else {
        const data = new TeamCreate()
        data.name = this.name
        data.description = this.description || null
        const created = await teamsApi.createTeam(data)
        if (!created) throw new Error('Create failed')
        targetId = created.id
      }

      const desiredIds = new Set(this.memberIds)
      if (this.leadId) desiredIds.add(this.leadId)

      const existingIds = new Set(this._originalMembers.map(m => m.user_id))

      for (const uid of desiredIds) {
        if (!existingIds.has(uid)) {
          const role = uid === this.leadId ? 'lead' : 'member'
          await teamsApi.addMember(targetId, uid, role)
        }
      }

      for (const uid of existingIds) {
        if (!desiredIds.has(uid)) {
          await teamsApi.removeMember(targetId, uid)
        }
      }

      if (this.leadId && existingIds.has(this.leadId)) {
        const prevRole = this._originalMembers.find(m => m.user_id === this.leadId)?.role
        if (prevRole !== 'lead') {
          await teamsApi.updateMemberRole(targetId, this.leadId, 'lead')
        }
      }

      const oldLead = this._originalMembers.find(m => m.role === 'lead')
      if (oldLead && oldLead.user_id !== this.leadId && desiredIds.has(oldLead.user_id)) {
        await teamsApi.updateMemberRole(targetId, oldLead.user_id, 'member')
      }

      onSaved()
      onClose()
    } catch {
      showToast(i18n.t('usersTeams.saveError', { ns: 'settings' }), 'danger')
    } finally {
      this.reactTo.saving = false
    }
  }
}

/** Modal for creating or editing a team. */
export function TeamEditModal({ isOpen, teamId, users, onClose, onSaved }: TeamEditModalProps) {
  const { t } = useTranslation('settings')
  const state = useComponentState(TeamEditState)
  const isEdit = teamId != null

  useEffect(() => {
    if (isOpen) {
      state.reset()
      if (teamId) void state.loadTeam(teamId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamId])

  const title = isEdit ? t('usersTeams.teamModal.editTitle') : t('shared.fields.createTeam')

  // The search-select is controlled by entities, but the state stores ids. Map an
  // id back to a `UserBasic` (with its `displayName` getter) using the already-loaded
  // company `users`, so the picker can label the current lead/members. Unknown ids
  // (e.g. a member who has since left the company) fall back to a bare id-only record.
  function userById(id: string): UserBasic {
    const match = users.find(u => u.id === id)
    return Object.assign(new UserBasic(), {
      id,
      first_name: match?.first_name ?? null,
      last_name: match?.last_name ?? null,
      email: match?.email ?? null,
    })
  }

  const selectedLead = state.leadId ? [userById(state.leadId)] : []
  const selectedMembers = state.memberIds.map(userById)

  const footer = (
    <FormFooterActions
      onCancel={onClose}
      onSubmit={() => void state.save(teamId, onSaved, onClose)}
      submitting={state.saving}
      submitDisabled={!state.name.trim()}
      submitLabel={isEdit ? t('shared.actions.save') : t('shared.fields.createTeam')}
    />
  )

  return (
    <CardModal isOpen={isOpen} title={title} footer={footer} onClose={onClose}>
      <FormField label={t('shared.fields.name')}>
        <input
          type="text"
          className="form-control"
          value={state.name}
          onChange={e => { state.name = e.target.value }}
        />
      </FormField>

      <FormField label={t('shared.fields.description')}>
        <textarea
          className="form-control"
          rows={3}
          value={state.description}
          onChange={e => { state.description = e.target.value }}
        />
      </FormField>

      <FormField label={t('shared.fields.teamLead')} labelClassName="d-block">
        <UserSearchSelect
          selectedUsers={selectedLead}
          onSelectedUsersChange={next => { state.leadId = next[0]?.id ?? '' }}
          buttonLabel={<span className="ynt-select-placeholder">{t('shared.fields.teamLead')}</span>}
          buttonClassName="btn-secondary w-100 d-flex justify-content-between align-items-center"
          fullWidth
          searchPlaceholder={t('shared.fields.searchUsers')}
          emptyMessage={t('shared.fields.noUsersFound')}
        />
        <div className="form-text">{t('usersTeams.teamModal.leadHint')}</div>
      </FormField>

      <FormField label={t('shared.fields.teamMembers')} labelClassName="d-block" className="mb-0">
        <UserSearchSelect
          selectedUsers={selectedMembers}
          onSelectedUsersChange={next => { state.memberIds = next.map(u => u.id) }}
          multiple
          singleSelectionShowsLabel
          buttonLabel={<span className="ynt-select-placeholder">{t('shared.fields.teamMembers')}</span>}
          buttonClassName="btn-secondary w-100 d-flex justify-content-between align-items-center"
          fullWidth
          searchPlaceholder={t('shared.fields.searchUsers')}
          emptyMessage={t('shared.fields.noUsersFound')}
        />
      </FormField>
    </CardModal>
  )
}
